/**
 * Table geometry (TBL-001).
 *
 * Lives in `sim`, not in the renderer, because the physics needs it too: cushions are
 * what PHY-004 collides against, and pocket mouths are what PHY-005 tests. The
 * renderer is a consumer of this, never the owner of it.
 *
 * ⚠️ This is built with only `+ − × ÷` and `Math.sqrt`, like the stepping loop. It is
 * computed once rather than per step, but its output feeds collision arithmetic — so a
 * value derived from `Math.cos` here would diverge between engines just as surely as
 * one derived inside the loop (ARCHITECTURE.md §5.5).
 *
 * Coordinates: origin at the bottom-left of the playing surface, `+x` along the long
 * axis, `+y` across it. Always 2:1.
 */

import { DEFAULT_PHYSICS } from './config.ts';
export type TableSizeFt = 7 | 8 | 9;

export interface TableSpec {
  readonly sizeFt: TableSizeFt;
  /** Long axis, cushion to cushion. */
  readonly widthM: number;
  /** Short axis, cushion to cushion. Always widthM / 2. */
  readonly heightM: number;
  readonly pocketScale: number;
}

export interface Point {
  readonly xM: number;
  readonly yM: number;
}

/** A straight cushion face. PHY-004 collides against these. */
export interface CushionSegment {
  readonly from: Point;
  readonly to: Point;
  /** Unit normal pointing INTO the playing surface. */
  readonly nx: number;
  readonly ny: number;
}

export type PocketKind = 'corner' | 'side';

export interface Pocket {
  readonly kind: PocketKind;
  /** Centre of the throat — the point a ball must reach to drop. */
  readonly centre: Point;
  /** The two jaw tips, i.e. the ends of the mouth opening. */
  readonly mouthTips: readonly [Point, Point];
  /** Straight-line width of the mouth, after `pocketScale`. */
  readonly mouthM: number;
  /** Radius of the throat circle. */
  readonly throatRadiusM: number;
  /** Midpoint of the mouth: the straight line between the two tips. */
  readonly mouthCentre: Point;
  /**
   * Unit vector pointing out of the table, through the mouth. A ball has only entered
   * the pocket once its centre is on this side of the mouth line (PHY-009).
   */
  readonly outward: Point;
  /**
   * The two angled jaw walls, running back from each mouth tip into the pocket. A ball
   * that enters off-centre meets one of these and is deflected — into the throat if it
   * arrived shallow, back out onto the table if it did not. This is where rattle comes
   * from; it is not a random roll (PHY-005).
   */
  readonly jaws: readonly [CushionSegment, CushionSegment];
}

export interface TableGeometry {
  readonly spec: TableSpec;
  readonly cushions: readonly CushionSegment[];
  readonly pockets: readonly Pocket[];
}

/**
 * Long axis of the playing surface, cushion to cushion.
 *
 * Derived from the INCH figures in the brief (78 / 88 / 100 in), not its metric
 * column. The two disagree: the metric values there are rounded to three decimals
 * (0.991 m for 39 in = 0.9906 m), which breaks the exact 2:1 ratio the brief also
 * requires — by a whole millimetre on a 7ft and 8ft table. The inch figures are the
 * published spec and are exactly 2:1, so they win.
 *
 * The short axis is derived as `widthM / 2`. Division by two is exact in binary
 * floating point, so the ratio holds to the last bit — which matters because the side
 * pockets sit at exactly `widthM / 2`.
 */
const LONG_AXIS_M: Record<TableSizeFt, number> = {
  7: 1.9812, // 78 in
  8: 2.2352, // 88 in
  9: 2.54, //   100 in
};

/**
 * Base mouth widths before `pocketScale` (brief §5). Side pockets are wider than the
 * corners, as on a real table; the owner kept that on 2026-09-25 after trying equal.
 */
const CORNER_MOUTH_M = 0.117;
const SIDE_MOUTH_M = 0.13;

/**
 * tan 14°: how far each side-pocket jaw wall leans in towards the pocket's centre line,
 * per metre of depth. A real side pocket's jaw meets the cushion face at 103–106°
 * (WPA equipment spec); 104° leans the wall 14° past square. Before PHY-009 the side
 * jaws met at a point, leaning 45°, which reached out of the mouth and turned balls
 * back at angles no real side pocket does.
 *
 * Baked in because `Math.tan` may differ across engines (CLAUDE.md §4). Derived as
 * `Math.tan(14 * Math.PI / 180)` in Node 24.
 */
const SIDE_JAW_LEAN = 0.24932800284318068;

/**
 * 1/√2, for the 45° corner mouth.
 *
 * This is the correctly-rounded double (identical to `Math.SQRT1_2`). Note that
 * computing `1 / Math.sqrt(2)` gives a DIFFERENT double, one ulp low, because the
 * division rounds a second time. Written as a literal so neither the engine nor the
 * expression can change it.
 */
const INV_SQRT2 = 0.7071067811865476;

/**
 * The pocket scale defaults to the physics config's, so every caller gets the same
 * pockets unless it asks otherwise. It used to default to 1: the web game got
 * true-to-life pockets while the layout generator used the config's value (L40).
 */
export function createTableSpec(
  sizeFt: TableSizeFt,
  pocketScale = DEFAULT_PHYSICS.pocketScale,
): TableSpec {
  const widthM = LONG_AXIS_M[sizeFt];
  return {
    sizeFt,
    widthM,
    heightM: widthM / 2,
    pocketScale,
  };
}

function segment(from: Point, to: Point, nx: number, ny: number): CushionSegment {
  return { from, to, nx, ny };
}

/**
 * Build the full geometry for a table: cushion faces, pocket mouths, the throat each
 * pocket opens into, and the angled jaw walls behind the mouth (PHY-005).
 *
 * Each pocket is a funnel. The two jaw walls run from the mouth tips back to a point
 * behind the throat, so a ball entering off-centre is deflected by one of them rather
 * than sailing through a hole. Whether it then reaches the throat depends on the angle
 * and speed it arrived at, which is exactly what rattle is.
 */
export function createTableGeometry(spec: TableSpec): TableGeometry {
  const { widthM: w, heightM: h, pocketScale } = spec;

  const cornerMouth = CORNER_MOUTH_M * pocketScale;
  const sideMouth = SIDE_MOUTH_M * pocketScale;

  // A corner mouth spans the 45° diagonal across the corner, so each jaw tip sits
  // this far along its rail from the corner itself.
  const cornerInset = cornerMouth * INV_SQRT2;
  const halfSide = sideMouth / 2;
  const midX = w / 2;

  const pockets: Pocket[] = [
    corner({ xM: 0, yM: 0 }, 1, 1, cornerInset, cornerMouth),
    corner({ xM: w, yM: 0 }, -1, 1, cornerInset, cornerMouth),
    corner({ xM: w, yM: h }, -1, -1, cornerInset, cornerMouth),
    corner({ xM: 0, yM: h }, 1, -1, cornerInset, cornerMouth),
    side({ xM: midX, yM: 0 }, halfSide, sideMouth, -1),
    side({ xM: midX, yM: h }, halfSide, sideMouth, 1),
  ];

  const cushions: CushionSegment[] = [
    // Bottom rail (y = 0), normal pointing up into the table.
    segment({ xM: cornerInset, yM: 0 }, { xM: midX - halfSide, yM: 0 }, 0, 1),
    segment({ xM: midX + halfSide, yM: 0 }, { xM: w - cornerInset, yM: 0 }, 0, 1),
    // Top rail (y = h), normal pointing down.
    segment({ xM: cornerInset, yM: h }, { xM: midX - halfSide, yM: h }, 0, -1),
    segment({ xM: midX + halfSide, yM: h }, { xM: w - cornerInset, yM: h }, 0, -1),
    // Left rail (x = 0), normal pointing right. No side pocket on the short rails.
    segment({ xM: 0, yM: cornerInset }, { xM: 0, yM: h - cornerInset }, 1, 0),
    // Right rail (x = w), normal pointing left.
    segment({ xM: w, yM: cornerInset }, { xM: w, yM: h - cornerInset }, -1, 0),
  ];

  return { spec, cushions, pockets };
}

function corner(
  at: Point,
  towardX: number,
  towardY: number,
  inset: number,
  mouthM: number,
): Pocket {
  const tips: [Point, Point] = [
    { xM: at.xM + towardX * inset, yM: at.yM },
    { xM: at.xM, yM: at.yM + towardY * inset },
  ];
  // Away from the table, along the corner's diagonal.
  const outward = { xM: -towardX * INV_SQRT2, yM: -towardY * INV_SQRT2 };
  // Both corner jaws run back to one apex behind the throat, making a funnel.
  const throatRadiusM = mouthM / 2;
  const apex: Point = {
    xM: at.xM + outward.xM * throatRadiusM,
    yM: at.yM + outward.yM * throatRadiusM,
  };
  return buildPocket('corner', at, tips, mouthM, outward, [apex, apex]);
}

function side(at: Point, half: number, mouthM: number, outwardY: number): Pocket {
  const tips: [Point, Point] = [
    { xM: at.xM - half, yM: at.yM },
    { xM: at.xM + half, yM: at.yM },
  ];
  // Each jaw runs one throat radius deep, leaning in by SIDE_JAW_LEAN. A ball's centre
  // has crossed the mouth line, and so dropped, well before it could reach the ends.
  const depthM = mouthM / 2;
  const lean = depthM * SIDE_JAW_LEAN;
  const backY = at.yM + outwardY * depthM;
  const ends: [Point, Point] = [
    { xM: at.xM - half + lean, yM: backY },
    { xM: at.xM + half - lean, yM: backY },
  ];
  return buildPocket('side', at, tips, mouthM, { xM: 0, yM: outwardY }, ends);
}

/**
 * Assemble a pocket, including the two jaw walls, each from a mouth tip to its end.
 *
 * Each wall's normal points back towards the mouth centre (the side the ball arrives
 * from), so a ball inside the funnel is pushed away from the wall, not through it.
 */
function buildPocket(
  kind: PocketKind,
  centre: Point,
  tips: [Point, Point],
  mouthM: number,
  outward: Point,
  jawEnds: [Point, Point],
): Pocket {
  const mouthCentre: Point = {
    xM: (tips[0].xM + tips[1].xM) / 2,
    yM: (tips[0].yM + tips[1].yM) / 2,
  };

  return {
    kind,
    centre,
    mouthTips: tips,
    mouthM,
    throatRadiusM: mouthM / 2,
    mouthCentre,
    outward,
    jaws: [jaw(tips[0], jawEnds[0], mouthCentre), jaw(tips[1], jawEnds[1], mouthCentre)],
  };
}

function jaw(from: Point, to: Point, facing: Point): CushionSegment {
  const dx = to.xM - from.xM;
  const dy = to.yM - from.yM;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length === 0) return segment(from, to, 0, 1);

  // Perpendicular, oriented towards `facing` — the open side of the funnel.
  let nx = -dy / length;
  let ny = dx / length;
  if (nx * (facing.xM - from.xM) + ny * (facing.yM - from.yM) < 0) {
    nx = -nx;
    ny = -ny;
  }
  return segment(from, to, nx, ny);
}

/** The playable surfaces once every jaw tip is rounded (PHY-010). */
export interface RoundedSurfaces {
  /** Rails and jaw walls, each shortened to where it meets its tip's curve. */
  readonly cushions: readonly CushionSegment[];
  /** Centre of each tip's curve: behind both faces, inside the rubber. */
  readonly tipCentres: readonly Point[];
}

const SAME_POINT_M = 1e-9;

function samePoint(a: Point, b: Point): boolean {
  const dx = a.xM - b.xM;
  const dy = a.yM - b.yM;
  return dx * dx + dy * dy < SAME_POINT_M * SAME_POINT_M;
}

function unit(from: Point, to: Point): Point {
  const dx = to.xM - from.xM;
  const dy = to.yM - from.yM;
  const length = Math.sqrt(dx * dx + dy * dy);
  return { xM: dx / length, yM: dy / length };
}

/**
 * Round every jaw tip into a curve of radius `tipRadiusM`, tangent to the rail face on
 * one side and the jaw face on the other: the shape of a real rubber jaw nose (PHY-010).
 *
 * Before this, the tip was a post of that radius centred ON the corner where the two
 * faces meet, so half of it stood proud of the cushion face, a knob about 13 mm into
 * the table at every pocket mouth. A ball rolling along the rail into a pocket hit the
 * knob and flew off as if it had struck a corner (the owner saw it), and every mouth
 * played narrower than its drawn width.
 *
 * Built at the start of a shot rather than in `createTableGeometry` so the tip radius
 * comes from the shot's physics config, which the debug panel can change (L40).
 *
 * Only `+ − × ÷` and `Math.sqrt`: the half-angle comes from the half-angle identities,
 * never from trig, so every engine builds the same surfaces (ARCHITECTURE.md §5.5).
 */
export function roundJawTips(geometry: TableGeometry, tipRadiusM: number): RoundedSurfaces {
  const rails = geometry.cushions.map((c) => ({ from: c.from, to: c.to, nx: c.nx, ny: c.ny }));
  const jaws: CushionSegment[] = [];
  const tipCentres: Point[] = [];

  for (const pocket of geometry.pockets) {
    for (let k = 0; k < 2; k++) {
      const tip = pocket.mouthTips[k];
      const jawWall = pocket.jaws[k];
      if (tip === undefined || jawWall === undefined) continue;

      // The rail that ends at this tip, and which of its ends that is.
      const railIndex = rails.findIndex((r) => samePoint(r.from, tip) || samePoint(r.to, tip));
      const rail = rails[railIndex];
      if (rail === undefined) {
        throw new Error(`roundJawTips: no rail ends at the ${pocket.kind} pocket's tip`);
      }
      const railFar = samePoint(rail.from, tip) ? rail.to : rail.from;

      // Along each face, away from the tip. The rubber lies between the two.
      const alongRail = unit(tip, railFar);
      const alongJaw = unit(tip, jawWall.to);
      const cosAngle = alongRail.xM * alongJaw.xM + alongRail.yM * alongJaw.yM;
      const sinHalf = Math.sqrt((1 - cosAngle) / 2);
      const cosHalf = Math.sqrt((1 + cosAngle) / 2);

      // Tangent points: this far from the tip along each face.
      const tangentM = (tipRadiusM * cosHalf) / sinHalf;
      // The curve's centre: along the bisector, into the rubber.
      const bisector = unit(
        { xM: 0, yM: 0 },
        {
          xM: alongRail.xM + alongJaw.xM,
          yM: alongRail.yM + alongJaw.yM,
        },
      );
      const centreM = tipRadiusM / sinHalf;

      const onRail: Point = {
        xM: tip.xM + alongRail.xM * tangentM,
        yM: tip.yM + alongRail.yM * tangentM,
      };
      const onJaw: Point = {
        xM: tip.xM + alongJaw.xM * tangentM,
        yM: tip.yM + alongJaw.yM * tangentM,
      };

      if (samePoint(rail.from, tip)) rails[railIndex] = { ...rail, from: onRail };
      else rails[railIndex] = { ...rail, to: onRail };
      jaws.push({ from: onJaw, to: jawWall.to, nx: jawWall.nx, ny: jawWall.ny });
      tipCentres.push({
        xM: tip.xM + bisector.xM * centreM,
        yM: tip.yM + bisector.yM * centreM,
      });
    }
  }

  return { cushions: [...rails, ...jaws], tipCentres };
}
